import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { columnsApi, errorMessage } from '../../lib/api.ts'
import type { ColumnColor } from '../../lib/types.ts'
import { ErrorState } from '../../ui/primitives.tsx'
import styles from './board.module.css'

const COLORS: { value: ColumnColor; label: string }[] = [
  { value: 'gray', label: 'szary' },
  { value: 'blue', label: 'niebieski' },
  { value: 'steel', label: 'stalowy' },
  { value: 'green', label: 'zielony' },
]

/**
 * The "+ Dodaj kolumnę" tile (ui-spec §5.2).
 *
 * It was a `<div>` with no handler: styled like a button, captioned "Nazwa, kolor, kolejność" —
 * promising a form — and wired to nothing. `POST /api/columns` had existed since stage 2, so the
 * missing half was only ever the front end.
 *
 * Order is not asked for: a new column goes to the end, which is the only place that needs no
 * decision. Moving it is a separate action and does not belong in a creation form.
 */
export function AddColumn(): React.JSX.Element {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [color, setColor] = useState<ColumnColor>('gray')

  const create = useMutation({
    mutationFn: (input: { name: string; color: ColumnColor }) => columnsApi.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['columns'] })
      setName('')
      setColor('gray')
      setOpen(false)
    },
  })

  const submit = (): void => {
    const trimmed = name.trim()
    if (trimmed) create.mutate({ name: trimmed, color })
  }

  if (!open) {
    return (
      <button type="button" className={styles.addColumn} onClick={() => setOpen(true)}>
        <span className={styles.addColumnTitle}>+ Dodaj kolumnę</span>
        <span className={styles.addColumnHint}>Nazwa i kolor. Board mieści 4–6 kolumn.</span>
      </button>
    )
  }

  return (
    <div className={styles.addColumn}>
      <input
        className={styles.addColumnInput}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- pole pojawia się po kliknięciu kafla
        autoFocus
        value={name}
        maxLength={40}
        placeholder="Nazwa kolumny"
        aria-label="Nazwa kolumny"
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit()
          if (e.key === 'Escape') setOpen(false)
        }}
      />

      <span className={styles.addColumnColors} role="radiogroup" aria-label="Kolor kolumny">
        {COLORS.map((option, index) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={color === option.value}
            aria-label={option.label}
            tabIndex={color === option.value ? 0 : -1}
            className={`${styles.addColumnSwatch} ${styles[`dot${option.value[0]!.toUpperCase()}${option.value.slice(1)}`] ?? ''}${
              color === option.value ? ` ${styles.addColumnSwatchOn}` : ''
            }`}
            onClick={() => setColor(option.value)}
            onKeyDown={(e) => {
              const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
              if (step === 0) return
              e.preventDefault()
              const next = COLORS[(index + step + COLORS.length) % COLORS.length]
              if (next) setColor(next.value)
            }}
          />
        ))}
      </span>

      <span className={styles.addColumnActions}>
        <button
          type="button"
          className={styles.addColumnConfirm}
          disabled={!name.trim() || create.isPending}
          onClick={submit}
        >
          Dodaj
        </button>
        <button
          type="button"
          className={styles.addColumnCancel}
          onClick={() => {
            setOpen(false)
            create.reset()
          }}
        >
          Anuluj
        </button>
      </span>

      {create.isError && (
        <ErrorState text={`Nie udało się dodać kolumny. ${errorMessage(create.error)}`} />
      )}
    </div>
  )
}
